import type {
  AgentAnswerPayload,
  AgentResultPayload,
  ExecutiveReportPayload,
} from '@/types/agent.types';

function isExecutiveShape(p: AgentResultPayload): p is ExecutiveReportPayload {
  return typeof (p as ExecutiveReportPayload).executive_summary === 'string';
}

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/**
 * Lleva cualquier payload guardado a la forma única.
 *
 * Los jobs creados antes de la unificación siguen en la base con la forma
 * ejecutiva (`executive_summary` + secciones sueltas). Se traducen al leerlos,
 * así que un historial viejo se sigue viendo sin conservar dos renderers.
 */
export function toAgentAnswer(payload: AgentResultPayload): AgentAnswerPayload {
  if (isExecutiveShape(payload)) {
    const sections = {
      key_findings: nonEmpty(payload.key_findings),
      root_cause: nonEmpty(payload.root_cause),
      priority_recommendations: nonEmpty(payload.priority_recommendations),
    };
    const hasDepth = Object.values(sections).some(Boolean);
    return {
      insight_text: payload.executive_summary,
      sections: hasDepth ? sections : null,
      display_config: payload.display_config ?? null,
      metadata: payload.metadata ?? null,
      language: payload.language,
    };
  }

  const answer = payload as AgentAnswerPayload;
  const sections = answer.sections ?? null;
  const hasDepth =
    sections != null &&
    Boolean(sections.key_findings || sections.root_cause || sections.priority_recommendations);

  return {
    status: answer.status,
    insight_text: answer.insight_text ?? '',
    sections: hasDepth ? sections : null,
    display_config: answer.display_config ?? null,
    raw_data: answer.raw_data,
    metadata: answer.metadata ?? null,
    language: answer.language,
  };
}
